import { createSlice } from "@reduxjs/toolkit";
import moment from "moment";

export interface OrderState {
  receivedOrders: any[];
  processedOrders: any[];
  finishedOrders: any[];

  newOrderFailed: boolean;
  loadingNewOrder: boolean;

  ordersFailed: boolean;
  loadingOrders: boolean;

  orderReceived: boolean;
  orderPending: boolean;

  finishOrderFailed: boolean;
  loadingFinishOrder: boolean;

  processOrderFailed: boolean;
  processingOrder: boolean;

  prepareOrderFailed: boolean;
  preparingOrder: boolean;

  articleAvailability: null;

  transactionsFailed: boolean;
  loadingTransactions: boolean;

  activateWhistle: boolean;

  thisOrder: any;

  newOrderNotification: boolean;
}

const initialState: OrderState = {
  receivedOrders: [],
  processedOrders: [],
  finishedOrders: [],

  newOrderFailed: false,
  loadingNewOrder: false,

  ordersFailed: false,
  loadingOrders: false,

  orderReceived: true,
  orderPending: false,

  finishOrderFailed: false,
  loadingFinishOrder: false,

  processOrderFailed: false,
  processingOrder: false,

  prepareOrderFailed: false,
  preparingOrder: false,

  articleAvailability: null,

  transactionsFailed: false,
  loadingTransactions: false,

  activateWhistle: false,

  thisOrder: null,

  newOrderNotification: false,
};

const orderSlice = createSlice({
  name: "orderSlice",
  initialState: initialState,
  reducers: {
    orderReceived: (state, action) => {
      state.orderReceived = action.payload;
    },
    orderPending: (state, action) => {
      state.orderPending = action.payload;
      state.orderReceived = false;
    },
    fetchNewOrderStart: (state) => {
      state.loadingNewOrder = true;
      state.newOrderFailed = false;
    },
    newOrderReceived: (state, action) => {
      state.receivedOrders = [...state.receivedOrders, action.payload];
      state.loadingNewOrder = false;
      state.newOrderFailed = false;
      state.activateWhistle = true;
      // state.playSound = true;
    },
    fetchNewOrderFailed: (state) => {
      state.loadingNewOrder = false;
      state.newOrderFailed = true;
    },
    fetchOrdersStart: (state) => {
      state.ordersFailed = false;
      state.loadingOrders = true;
    },
    fetchOrdersFailed: (state) => {
      state.ordersFailed = false;
      state.loadingOrders = true;
    },
    orders: (state, action) => {
      // order.status 'CREATED' 'PROCESSED'-u obradi 'PREPARED'-spakovano
      // TRANSAKCIJE 'CANCELED' 'REJECTED' 'TAKEN'
      state.receivedOrders = [];
      state.processedOrders = [];
      let sortedArray = [...action.payload];
      sortedArray.sort((a, b) =>
        moment(a.created).isBefore(moment(b.created)) ? 1 : -1,
      );
      sortedArray.forEach((order: any) =>
        (order.status === "PROCESSED" || order.status === "PREPARED"
          ? state.processedOrders
          : state.receivedOrders
        ).push(order),
      );
      state.ordersFailed = false;
      state.loadingOrders = false;
    },
    driverOrders: (state, action) => {
      const userId = localStorage.getItem("id");
      state.finishedOrders = [];
      state.receivedOrders = [];
      state.processedOrders = [];
      action.payload.forEach((order: any) => {
        if (order.userDeliveryResponse === null) {
          state.receivedOrders.push(order);
        } else {
          // Favourite-volunteer orders arrive already assigned while still
          // CREATED; only the assigned volunteer gets to see them (reserved,
          // waiting for the pharmacy), other drivers must not.
          if (
            order.status === "CREATED" &&
            order.userDeliveryResponse.id == userId
          ) {
            state.receivedOrders.push(order);
          }

          if (
            (order.status === "IN_DELIVERY" || order.status === "PREPARED") &&
            order.userDeliveryResponse.id == userId
          ) {
            state.processedOrders.push(order);
          }

          if (
            order.status === "TAKEN" &&
            order.userDeliveryResponse.id == userId
          ) {
            state.finishedOrders.push(order);
          }
        }
      });

      state.ordersFailed = false;
      state.loadingOrders = false;
    },
    adminDriverOrders: (state, action) => {
      state.receivedOrders = [];
      state.processedOrders = [];
      let sortedArray = [...action.payload];
      sortedArray.sort((a, b) =>
        moment(a.created).isBefore(moment(b.created)) ? 1 : -1,
      );
      sortedArray
        .filter((order: any) => order.userAddressResponse)
        .forEach((order: any) => {
          if (
            (order.status === "PREPARED" || order.status === "IN_DELIVERY") &&
            order.userDeliveryResponse != null
          ) {
            state.processedOrders.push(order);
          } else if (
            order.status === "CREATED" ||
            (order.status === "PREPARED" && order.userDeliveryResponse == null)
          ) {
            state.receivedOrders.push(order);
          }
        });
      state.ordersFailed = false;
      state.loadingOrders = false;
    },
    toggleOrderModal: (state, action) => {
      state.thisOrder = action.payload;
    },
    processOrder: (state, action) => {
      state.receivedOrders = state.receivedOrders.filter((order) => {
        if (order.id === action.payload.orderId) {
          state.processedOrders.push({
            ...order,
            status: "PROCESSED",
            willBeReadyIn: action.payload.responseData.willBeReadyIn,
            willBeReadyInDate: action.payload.responseData.willBeReadyInDate,
            modified: action.payload.responseData.modified,
            noteForUser: action.payload.noteForUser,
          });
        } else {
          return order;
        }
      });

      state.processOrderFailed = false;
      state.processingOrder = false;
    },
    processOrderFailed: (state) => {
      state.processOrderFailed = true;
      state.processingOrder = false;
    },
    processOrderStart: (state) => {
      state.processOrderFailed = false;
      state.processingOrder = true;
    },
    preparedOrder: (state, action) => {
      state.processedOrders = state.processedOrders.map((order) => {
        if (order.id === action.payload) {
          return {
            ...order,
            status: "PREPARED",
          };
        } else {
          return order;
        }
      });
    },
    preparedOrderFailed: (state) => {
      state.prepareOrderFailed = true;
      state.preparingOrder = false;
    },
    preparedOrderStart: (state) => {
      state.prepareOrderFailed = false;
      state.preparingOrder = true;
    },
    finishOrderStart: (state) => {
      state.loadingFinishOrder = true;
      state.finishOrderFailed = false;
    },
    finishOrder: (state, action) => {
      state.processedOrders = state.processedOrders.filter((order) => {
        if (order.id === action.payload.cncOrderId) {
          if (action.payload.status === "CANCELED") {
            state.finishedOrders = [
              ...state.finishedOrders,
              {
                ...order,
                status: action.payload.status,
                noteForCanceledStatus: action.payload.note,
              },
            ];
          } else {
            state.finishedOrders = [
              ...state.finishedOrders,
              { ...order, status: action.payload.status },
            ];
          }
        } else {
          return order;
        }
      });
      state.loadingFinishOrder = false;
      state.finishOrderFailed = false;
    },
    rejectOrder: (state, action) => {
      state.receivedOrders = state.receivedOrders.filter((order) => {
        if (order.id === action.payload.cncOrderId) {
          state.finishedOrders = [
            ...state.finishedOrders,
            { ...order, status: "REJECTED", noteForUser: action.payload.note },
          ];
        } else {
          return order;
        }
      });
      state.loadingFinishOrder = false;
      state.finishOrderFailed = false;
    },
    finishOrderFailed: (state) => {
      state.loadingFinishOrder = false;
      state.finishOrderFailed = true;
    },
    fetchTransactionsStart: (state) => {
      state.transactionsFailed = false;
      state.loadingTransactions = true;
    },
    transactions: (state, action) => {
      let transactions = action.payload;
      const cutoff = moment().subtract(72, "hours");
      transactions = transactions.filter((transaction: any) =>
        moment(transaction.created).isAfter(cutoff),
      );
      transactions = transactions
        .sort((a: any, b: any) =>
          moment(a.created).isBefore(moment(b.created)) ? 1 : -1,
        )
        .map((transaction: any) => {
          return {
            ...transaction,
            id: transaction.orderNumber,
            transactionNumber: transaction.id,
            // Map important timestamp fields for display in archived column
            orderCreated: transaction.orderCreated || transaction.created,
            orderProcessed: transaction.orderProcessed,
            created: transaction.created, // This is the timestamp of the transaction itself (when order was taken/completed)
            status: transaction.status,
          };
        });
      state.finishedOrders = [...transactions];
      state.transactionsFailed = false;
      state.loadingTransactions = false;
    },
    adminTransactions: (state, action) => {
      let transactions = action.payload;
      // No 72-hour cutoff for admin users - show all transactions
      transactions = transactions
        .sort((a: any, b: any) =>
          moment(a.created).isBefore(moment(b.created)) ? 1 : -1,
        )
        .map((transaction: any) => {
          return {
            ...transaction,
            id: transaction.orderNumber,
            transactionNumber: transaction.id,
            // Map important timestamp fields for display in archived column
            orderCreated: transaction.orderCreated || transaction.created,
            orderProcessed: transaction.orderProcessed,
            created: transaction.created, // This is the timestamp of the transaction itself (when order was taken/completed)
            status: transaction.status,
          };
        });
      state.finishedOrders = [...transactions];
      state.transactionsFailed = false;
      state.loadingTransactions = false;
    },
    fetchTransactionsFailed: (state) => {
      state.transactionsFailed = true;
      state.loadingTransactions = false;
    },
    clearOrdersAndTransactions: (state) => {
      state.receivedOrders = [];
      state.processedOrders = [];
      state.finishedOrders = [];
    },
    newOrderDesktopNotification: (state, action) => {
      state.newOrderNotification = action.payload;
    },
  },
});

export const orderActions = orderSlice.actions;

export default orderSlice;
